import { apiToEditTransactions } from '@api/apiCall';
import CommonTextInput from '@atoms/CommonTextInput';
// import InfoTextComponent from '@atoms/InfoTextComponent';
import { Button, Text } from '@components/primitives';
import useBeforeUnload from '@customHooks/useBeforeUnload';
import EditReasons from '@molecules/EditReasons';
import DetailsComponent from '@molecules/EditTransactions/DetailsComponent';
// import ImageUPloadComponent from '@molecules/EditTransactions/ImageUploadComponent';
import TokenContainer from '@molecules/EditTransactions/TokenContainer';
import VoucherCode from '@molecules/EditTransactions/VoucherCode';
import { useNavigation, useRoute } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import { useMutation } from '@tanstack/react-query';
import colors from '@theme/colors';
import AmplitudeHelper from '@utils/analytics';
import { AMPLITUDE_CONSTANTS, AMPLITUDE_EVENT_SOURCES } from '@utils/analytics/constants';
// import { scale } from '@utils/normalize';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SafeAreaView, ScrollView, View } from 'react-native';
import { logDetailPage, logFocus } from './analyticsEditTransaction';
import styles from './styles';
import { scale } from '@utils/normalize';

export function shouldRenderTitle(title: string) {
  return (
    <View style={styles.titleStyle}>
      <Text variant="sm-bold" color={colors.neutral.darkMist}>
        {title}
      </Text>
    </View>
  );
}

export function getChangedField(
  oldReason?: string,
  newReason?: string,
  oldDesc?: string,
  newDesc?: string,
): string | false {
  if (!newReason) return false;
  if (oldReason !== newReason && oldDesc !== newDesc) return 'notes,reason';
  if (oldReason !== newReason) return 'reason';
  if (oldDesc !== newDesc) return 'notes';
  return false;
}

export function handleUnload(navigation: { goBack: () => void }) {
  return () => navigation.goBack();
}

export function getDescTitleText(accountTypeId: number, t: (key: string) => string): string {
  if (accountTypeId === 4) return t('transactionDetailsScreen.uangdescTitle');
  return t('transactionDetailsScreen.keterangan');
}

export function getParameterChanged(
  isImageAdded: boolean,
  detailsData: {
    description?: string;
    transReason?: string;
  },
  withdrawalReason?: { reason?: string },
  description?: string,
): string | false {
  return isImageAdded && !detailsData?.description
    ? 'image_added'
    : getChangedField(detailsData?.transReason, withdrawalReason?.reason, detailsData?.description, description);
}

export function onSimpanPressHandler(
  editTransactioCall: (args: {
    transactionHistoryId: number;
    transactionReasonId?: number;
    description?: string;
    accountNumber: number;
  }) => void,
  detailsData: {
    transHistoryId: number;
  },
  withdrawalReason?: { id?: number },
  description?: string,
  selectedTxnAccount?: { id?: number },
) {
  return () => {
    editTransactioCall({
      transactionHistoryId: detailsData.transHistoryId,
      transactionReasonId: withdrawalReason?.id,
      description,
      accountNumber: selectedTxnAccount?.id ?? 0,
    });
  };
}

export function onDeleteHandler(setImageAdded: (val: boolean) => void) {
  return () => setImageAdded(false);
}

export function onUploadHandler(setImageAdded: (val: boolean) => void) {
  return () => setImageAdded(true);
}

export function onSucessHandler(
  isSuccess: boolean,
  detailsData: {
    transactionId?: string | number;
    transReason?: string;
    description?: string;
  },
  isImageAdded: boolean,
  withdrawalReason: { reason?: string },
  description: string,
  navigation: { goBack: () => void },
  setEditRefresh: (val: boolean) => void,
) {
  if (isSuccess) {
    const e = AMPLITUDE_CONSTANTS.TransactionPage.transaction_edit_saved;
    AmplitudeHelper.logEvent(e.name, {
      [e.attributes.source]: AMPLITUDE_EVENT_SOURCES.transaction_page,
      [e.attributes.transaction_id]: detailsData?.transactionId ?? null,
      [e.attributes.parameter_changed]: getParameterChanged(isImageAdded, detailsData, withdrawalReason, description),
      [e.attributes.old_reason_input]: detailsData?.transReason ?? null,
      [e.attributes.new_reason_input]: withdrawalReason?.reason ?? '',
      [e.attributes.old_description_input]: detailsData?.description ?? null,
      [e.attributes.new_description_input]: description ?? null,
    });
    navigation.goBack();
    setEditRefresh(true);
  }
}

function GGEditTransaction(): JSX.Element {
  const route = useRoute<any>();

  const {
    params: { detailsConfig = [], detailsData, accountTypeId },
  } = route;

  const [isImageAdded] = useState<boolean>(false);

  const {
    withdrawalReason,
    setWithdrawalReason,
    selectedTxnAccount,
    setEditRefresh,
  }: {
    withdrawalReason: { reason?: string; id?: number } | null;
    setWithdrawalReason: (val: any) => void;
    selectedTxnAccount?: { id?: number };
    setEditRefresh: (val: boolean) => void;
  } = getStore();

  const { t } = useTranslation('common');
  const navigation = useNavigation<any>();

  const [description, setDescription] = useState<string>(detailsData.description ?? '');

  const {
    isLoading: editTrasnactionloading = false,
    isSuccess,
    mutate: editTransactioCall,
  } = useMutation({
    mutationFn: (payload: any) => apiToEditTransactions(payload),
  });

  useEffect(() => {
    onSucessHandler(
      isSuccess,
      detailsData,
      isImageAdded,
      withdrawalReason ?? {},
      description,
      navigation,
      setEditRefresh,
    );
  }, [isSuccess, setEditRefresh]);

  useEffect(() => () => setWithdrawalReason(null), []);

  useEffect(() => {
    logDetailPage(detailsData);
  }, []);

  useBeforeUnload(handleUnload(navigation));

  return (
    <SafeAreaView style={appStyles.safeAreaView}>
      <ScrollView contentContainerStyle={styles.scrollViewContainerStyle}>
        <EditReasons
          disabled={detailsData.canEditReason === false}
          iconUrl={detailsData.iconUrl}
          transReason={detailsData?.transReason}
          transReasonId={detailsData?.transactionReasonId}
          position={detailsData?.position}
        />
        <TokenContainer title={detailsData?.displayName} token={detailsData.token} />
        <VoucherCode redeemUrl={detailsData.redeemUrl} />
        {shouldRenderTitle(t('transactionDetailsScreen.detailTransaksi'))}
        <DetailsComponent detailsData={detailsData} detailsConfig={detailsConfig} />
        {shouldRenderTitle(getDescTitleText(accountTypeId, t))}
        <CommonTextInput
          style={styles.textAreaStyles}
          multiline
          value={description}
          onChangeText={setDescription}
          onFocus={logFocus}
          placeholder={t('transactionDetailsScreen.masukkanKeterangan')}
          placeholderTextColor={colors.neutral.darkMist}
        />
      </ScrollView>
      <View style={[appStyles.bottomView, styles.buttonViewStyle]}>
        <Button
          isLoading={editTrasnactionloading}
          isDisabled={
            !getChangedField(detailsData?.transReason, withdrawalReason?.reason, detailsData?.description, description)
          }
          marginX="16px"
          textAlign="center"
          testID="editTransactionBtn"
          paddingY={scale(8)}
          onPress={onSimpanPressHandler(
            editTransactioCall,
            detailsData,
            withdrawalReason ?? {},
            description,
            selectedTxnAccount,
          )}>
          <Text testID="txtWhatsapp" variant="sm-bold" color={colors.neutral.cotton}>
            {t('transactionDetailsScreen.simpan')}
          </Text>
        </Button>
      </View>
    </SafeAreaView>
  );
}

export default GGEditTransaction;
